import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  fetchCreditRules,
  deleteCreditRule,
  updateCreditRule,
} from "../../../../../api/finances/guardianAPI";
import Tooltip from "../../../../../components/ui/Tooltip";
import ActionMenu from "../../../../../components/ui/ActionMenu";
import ConfirmationModal from "../../../../../components/ui/popup/ConfirmationModal";
import LoadingToRealTransitionWrapper from "../../../../../components/ui/transitions/LoadingToRealTransitionWraper";
import GhostSpendSafeguards from "../../../../../components/ui/loading/skeletons/GhostSpendSafeguards";
import { getSafeguardRuleLimit } from "../../../../../utils/configs/finances/planLimits";

function CreditSafeguards({
  content,
  isPaidPlan,
  refreshTrigger,
  onUpgradeClick,
  onAddRuleClick,
  onEditRuleClick,
}) {
  const guardian = content.subscriber_view.money_guardian;
  const copy = guardian.credit_safeguards;

  const planTier = useSelector(
    (state) => state.auth.user?.financial_insights?.plan_code || "fi_free",
  );

  const [creditRules, setCreditRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // --- Confirmation Modal State ---
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [confirmModalError, setConfirmModalError] = useState("");

  const [pendingAction, setPendingAction] = useState({
    mode: null,
    rule: null,
  });

  // Calculate dynamic limit and limit state
  const maxRulesAllowed = getSafeguardRuleLimit(planTier, "credit_safeguards");
  const isLimitReached = creditRules.length >= maxRulesAllowed;

  const loadRules = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      setErrorMessage(null);
      const response = await fetchCreditRules();

      if (response.success) {
        const sortedRules = [...response.data].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        setCreditRules(sortedRules);
      } else {
        setErrorMessage(response.error || guardian.errors.load_fail);
      }
    } catch (err) {
      setErrorMessage(guardian.errors.unexpected);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, [refreshTrigger]);

  const handleToggleClick = (ruleUuid, ruleName, currentIsEnable) => {
    setConfirmModalError("");
    setPendingAction({
      mode: "toggle",
      rule: { uuid: ruleUuid, name: ruleName, isEnable: currentIsEnable },
    });
    setIsConfirmModalOpen(true);
  };

  const handleDeleteClick = (ruleUuid, ruleName) => {
    setConfirmModalError("");
    setPendingAction({
      mode: "delete",
      rule: { uuid: ruleUuid, name: ruleName },
    });
    setIsConfirmModalOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!pendingAction.mode || !pendingAction.rule) return;

    const { mode, rule } = pendingAction;
    setConfirmModalError("");
    setIsProcessingAction(true);

    if (mode === "toggle") {
      const updatedStatus = !rule.isEnable;
      const result = await updateCreditRule(rule.uuid, {
        isEnable: updatedStatus,
      });

      if (result.success) {
        setCreditRules((prev) =>
          prev.map((r) =>
            r.uuid === rule.uuid ? { ...r, isEnable: updatedStatus } : r,
          ),
        );
        setIsConfirmModalOpen(false);
        setPendingAction({ mode: null, rule: null });
        loadRules(true);
      } else {
        setConfirmModalError(result.error || guardian.errors.toggle_fail);
      }
    } else if (mode === "delete") {
      const result = await deleteCreditRule(rule.uuid);

      if (result.success) {
        setIsConfirmModalOpen(false);
        setPendingAction({ mode: null, rule: null });
        loadRules();
      } else {
        setConfirmModalError(result.error || guardian.errors.delete_fail);
      }
    }

    setIsProcessingAction(false);
  };

  if (errorMessage) {
    return (
      <div className="error-text-container" style={{ padding: "16px" }}>
        {errorMessage}
      </div>
    );
  }

  const targetRuleName = pendingAction.rule?.name;
  let modalMessage = "";
  let confirmCtaText = isProcessingAction
    ? guardian.modals.processing_cta
    : guardian.actions.update;

  if (pendingAction.mode === "toggle") {
    const incomingActionState = pendingAction.rule?.isEnable
      ? guardian.labels.disabled
      : guardian.labels.enabled;

    modalMessage = guardian.modals.toggle_message
      .replace("{action}", incomingActionState.toLowerCase())
      .replace("{name}", targetRuleName);

    confirmCtaText = isProcessingAction
      ? guardian.modals.processing_cta
      : incomingActionState;
  } else if (pendingAction.mode === "delete") {
    modalMessage = guardian.modals.delete_message.replace(
      "{name}",
      targetRuleName,
    );
    confirmCtaText = isProcessingAction
      ? guardian.modals.deleting_cta
      : guardian.actions.delete;
  }

  const renderDropdownToggle = (rule) => {
    const uniqueSwitchId = `dropdown-switch-credit-${rule.uuid}`;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: "16px",
        }}
      >
        <span>
          {rule.isEnable ? guardian.labels.enabled : guardian.labels.disabled}
        </span>
        <div className="switch-container" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            id={uniqueSwitchId}
            className="edit-switch"
            checked={rule.isEnable}
            onChange={() =>
              handleToggleClick(rule.uuid, rule.name, rule.isEnable)
            }
          />
          <label htmlFor={uniqueSwitchId} className="switch-label">
            <span className="switch-slider"></span>
          </label>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <div className="standard-container" style={{ marginTop: "24px" }}>
        <div className="guardian-section-header">
          <Tooltip content={copy.desc_tooltip}>
            <h4 className="h4-tooltip">{copy.title}</h4>
          </Tooltip>
          <div style={{ marginTop: "16px" }}>
            {isLimitReached && !isPaidPlan ? (
              <p className="reset-margin free-plan-msg">
                <Tooltip content={copy.unsubscribe_add_rule_marketing}>
                  <span
                    style={{
                      color: "#B45309",
                      cursor: "pointer",
                      textDecoration: "underline dashed",
                    }}
                  >
                    {copy.unsubscribe_add_rule_msg}
                  </span>
                </Tooltip>
                <span
                  onClick={onUpgradeClick}
                  style={{
                    marginLeft: "10px",
                    color: "#3b82f6",
                    cursor: "pointer",
                  }}
                >
                  {copy.unsubscribe_add_rule_cta}
                </span>
              </p>
            ) : isLimitReached ? (
              <span style={{ fontSize: "12px", color: "#6B7280" }}>
                Maximum limit reached ({creditRules.length}/{maxRulesAllowed}{" "}
                rules)
              </span>
            ) : (
              <button
                className="cta-button primary small"
                onClick={onAddRuleClick}
                style={{
                  padding: "8px 14px",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                {copy.add_rule_cta}
              </button>
            )}
          </div>
        </div>

        <div className="rules-list-group">
          <LoadingToRealTransitionWrapper
            isLoading={isLoading && creditRules.length === 0}
            ghost={<GhostSpendSafeguards count={2} />}
            disableDefaultStyles={true}
            style={{ gap: "0px" }}
          >
            {creditRules.map((rule, index) => {
              const rowKey = `row-credit-${index}-${rule.uuid}`;

              const customRuleActions = [
                {
                  label: renderDropdownToggle(rule),
                  onClick: () =>
                    handleToggleClick(rule.uuid, rule.name, rule.isEnable),
                },
                {
                  label: guardian.actions.update,
                  onClick: () => onEditRuleClick(rule),
                },
                {
                  label: guardian.actions.delete,
                  className: "delete-action-item",
                  onClick: () => handleDeleteClick(rule.uuid, rule.name),
                },
              ];

              return (
                <div
                  className="rule-item"
                  key={rowKey}
                  style={{
                    borderBottom:
                      index === creditRules.length - 1
                        ? "none"
                        : "1px solid #E5E7EB",
                  }}
                >
                  <div className="rule-item-left">
                    <div className="rule-item-left-name-container">
                      <span className="rule-item-name-text">{rule.name}</span>
                      <span className="rule-item-text-badget-custom">
                        {guardian.labels.custom}
                      </span>
                      {!rule.isEnable && (
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 500,
                            padding: "2px 6px",
                            borderRadius: "4px",
                            backgroundColor: "#F3F4F6",
                            color: "#6B7280",
                            marginLeft: "6px",
                          }}
                        >
                          {guardian.labels.disabled}
                        </span>
                      )}
                    </div>
                    <span className="rule-item-description">
                      {rule.description}
                    </span>
                  </div>

                  <div
                    className="rule-item-right"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <Tooltip content={copy.threshold_tooltip}>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: rule.isEnable ? "#1F2937" : "#9CA3AF",
                          borderBottom: `1px dashed ${
                            rule.isEnable ? "#1F2937" : "#9CA3AF"
                          }`,
                        }}
                      >
                        $
                        {rule.amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </Tooltip>
                    <div className="item-actions">
                      <ActionMenu actions={customRuleActions} />
                    </div>
                  </div>
                </div>
              );
            })}

            {!isLoading && creditRules.length === 0 && (
              <div
                style={{
                  padding: "24px",
                  textAlign: "center",
                  color: "#6B7280",
                  fontSize: "14px",
                }}
              >
                {guardian.labels.no_rules}
              </div>
            )}
          </LoadingToRealTransitionWrapper>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setPendingAction({ mode: null, rule: null });
        }}
        onConfirm={handleConfirmAction}
        message={modalMessage}
        confirmText={confirmCtaText}
        cancelText={guardian.modals.cancel_cta}
        confirmButtonClass="primary"
        cancelButtonClass="secondary"
        isLoading={isProcessingAction}
        error={confirmModalError}
      />
    </Fragment>
  );
}

CreditSafeguards.propTypes = {
  content: PropTypes.object.isRequired,
  isPaidPlan: PropTypes.bool.isRequired,
  refreshTrigger: PropTypes.number.isRequired,
  onUpgradeClick: PropTypes.func.isRequired,
  onAddRuleClick: PropTypes.func.isRequired,
  onEditRuleClick: PropTypes.func.isRequired,
};

export default CreditSafeguards;
